import { useAuthStore } from '@/stores/auth';
import { useClientPortalStore } from '@/stores/clientPortal';
import { useTenantStore } from '@/stores/tenant';
import { useUserStore } from '@/stores/userStore';

export default defineNuxtPlugin(() => {
    const authStore = useAuthStore();
    const tenantStore = useTenantStore();
    const portalStore = useClientPortalStore();
    const userStore = useUserStore();
    const router = useRouter();
    authStore.setupAuthListener();

    const resetWorkspaceContext = () => {
        tenantStore.clearTenants();
        portalStore.clearAccess();
        userStore.clearUser();
    };

    const isProtectedPath = (route: ReturnType<typeof useRouter>['currentRoute']['value']): boolean => {
        if (route.meta?.publicAccess === true) return false;

        const path = route.path;
        if (path === '/google-calendar/callback') return false;
        if (path === '/google-gmail/callback') return false;
        if (path === '/public' || path.startsWith('/public/')) return false;
        if (path === '/portal' || path.startsWith('/portal/')) return true;
        // Protect any tenant-scoped route: /:tenantSlug/<page>
        // A tenant path has at least two segments and is not an auth or public route
        const segments = path.split('/').filter(Boolean);
        const firstSegment = segments[0] || '';
        const publicPrefixes = ['auth', 'accept-invite', 'accept-client-invite', 'portal', 'public'];
        return segments.length >= 2 && !publicPrefixes.includes(firstSegment);
    };

    const getLoginRoute = (path: string): string => {
        if (path === '/portal' || path.startsWith('/portal/')) {
            return '/auth/client-login';
        }
        return '/auth/login';
    };

    const redirectUser = async() => {
        const route = router.currentRoute.value;
        const { path } = route;

        if (!authStore.user) {
            resetWorkspaceContext();
            if (isProtectedPath(route)) {
                await router.push({
                    path: getLoginRoute(path),
                    query: {
                        redirect: route.fullPath || path || '/',
                    },
                });
            }
        }
    };

    authStore.awaitAuthReady().finally(() => {
        void redirectUser();
    });

    watch(() => authStore.user?.uid || null, (newUid, oldUid) => {
        if (newUid !== oldUid) {
            resetWorkspaceContext();
            if (oldUid) {
                // A new document discards every store, listener and in-flight callback
                // belonging to the previous identity, including same-tenant logins.
                window.location.replace(newUid ? router.currentRoute.value.fullPath : getLoginRoute(router.currentRoute.value.path));
                return;
            }
        }
        void redirectUser();
    }, { flush: 'sync' });
});
